import { type FC, useEffect } from 'react';
import { Spinner } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { useDeleteProductItem } from '../../../../bff-api/baskets/useDeleteProductItem';
import { useAddSaveForLater } from '../../../../bff-api/customers/useAddSaveForLater';
import { useCustomerGet } from '../../../../bff-api/customers/useCustomerGet';
import { useSaveForLater } from '../../../../bff-api/customers/useSaveForLater';
import { isRegistered } from '../../../../contexts/utils/customer-utils';
import { useSLTToast } from '../../../../hooks/use-toast/useSLTToast';
import { Button, Text } from '../../../../ui-components';
import { useCustomPreferenceOrDefault, useUrl } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { PageUrl } from '../../../utils/page-url';
import { saveForLaterContainsProduct } from '../../utils/cart-utils';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';

const SAVE_FOR_LATER_PRODUCT_ID_PARAM = 'saveForLaterProductId';

interface SaveForLaterButtonProps {
    productItem: ProductItemEditor;
}

export const SaveForLaterButton: FC<SaveForLaterButtonProps> = ({ productItem }) => {
    const history = useHistory();
    const intl = useIntl();
    const {
        data,
        isFetching,
        isError: isCustomerGetError,
        error: customerGetError,
    } = useCustomerGet();
    const {
        data: saveForLaterList,
        isError: isSaveForLaterError,
        error: saveForLaterError,
    } = useSaveForLater();
    const addSaveForLater = useAddSaveForLater();
    const deleteProductItem = useDeleteProductItem('SAVE_FOR_LATER');
    const showToast = useSLTToast();
    const loginUrl = useUrl(PageUrl.LOGIN_SHOW);
    const isPwaAccountRedirectEnabled = useCustomPreferenceOrDefault(
        CustomPreference.PWA_ACCOUNT_REDIRECT,
        true,
    );

    if (isSaveForLaterError || isCustomerGetError) {
        throw saveForLaterError ?? customerGetError;
    }

    const { itemId, productId, c_online } = productItem;

    const onClick = async (): Promise<void> => {
        if (isRegistered(data)) {
            if (containsProduct) {
                showToast(
                    intl.formatMessage({
                        id: 'item.already.in.save.for.later',
                        defaultMessage: 'Item already in save for later',
                    }),
                );
            } else {
                await addSaveForLater.mutateAsync(productId);

                showToast(
                    intl.formatMessage({
                        id: 'item.added.to.save.for.later',
                        defaultMessage: 'Item added to save for later',
                    }),
                );

                await deleteProductItem.mutateAsync({ productItem, itemId });
            }
        } else {
            const url = isPwaAccountRedirectEnabled ? PageUrl.ACCOUNT_LOGIN : loginUrl;
            history.push(
                `${url}?redirect-url=${encodeURIComponent(
                    `${PageUrl.PWA_CART}?${SAVE_FOR_LATER_PRODUCT_ID_PARAM}=${productId}`,
                )}`,
            );
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (!isFetching && params.get(SAVE_FOR_LATER_PRODUCT_ID_PARAM) === productId) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            onClick();

            params.delete(SAVE_FOR_LATER_PRODUCT_ID_PARAM);

            history.replace({
                search: params.toString(),
            });
        }
    }, [isFetching, productId]);

    if (!c_online) {
        return null;
    }

    const containsProduct = saveForLaterContainsProduct(saveForLaterList, productId);

    const isLoading = addSaveForLater.isLoading || deleteProductItem.isLoading;

    return (
        <div className="flex items-center gap-1">
            {productItem.savedForLater && !isLoading ? (
                <Text id="item-already-saved">
                    <FormattedMessage
                        id="cart.item.secondary_buttons.item_already_saved"
                        defaultMessage="Item already in Save For Later"
                    />
                </Text>
            ) : (
                <Button
                    id={`cart-item-save-for-later-${productId}-button`}
                    variant="text-button-no-hover"
                    textSize="base"
                    onClick={onClick}
                    isDisabled={isLoading}
                >
                    <FormattedMessage
                        id="cart.item.secondary_buttons.save_for_later"
                        defaultMessage="Save For Later"
                    />
                </Button>
            )}

            {isLoading ? (
                <Spinner
                    data-testid={`cart-item-save-for-latter-${productId}-button-loading`}
                    size="xs"
                    color="slt-pepper.1000"
                />
            ) : null}
        </div>
    );
};
